import { useState } from 'react';
import { useT } from '@/store';
import {
  useArchiveOrderTemplate,
  useGenerateDueTemplates,
  useGenerateFromTemplate,
  useOrderTemplates,
  useSaveOrderTemplate,
} from '@/hooks/useOrderTemplates';
import { useAllQuarries, useSites } from '@/hooks/useDirectory';
import { EmptyState, ErrorState, Panel, SkeletonRows } from '@/components/ui/Panel';

/**
 * Periodinių (pasikartojančių) užsakymų šablonai — the standing jobs a
 * carrier repeats, and the one button that puts today's on the road.
 *
 * THE PROBLEM. A regular client on a regular route is the same order typed
 * again every morning: same quarry, same site, same material, same tonnage,
 * often the same rate. A dispatcher serving five standing customers retyped
 * between five and twenty orders a day, and every one of them was a fresh
 * chance to pick the wrong site off a dropdown that holds a hundred.
 *
 * WHY THERE IS A BUTTON AND NOT A SCHEDULER. The server does not generate
 * these on its own, and blueprints/order_templates.py explains at length
 * why — everything unattended in this product deliberately refuses MORE than
 * the attended path does, because at 06:00 there is nobody to read a
 * warning, and a template pointing at a quarry archived last week would put
 * a truck at a locked gate before anyone was awake. So the repetition is
 * stored, "these are due" is computed, and one click does the morning.
 *
 * The due count sits ON the button rather than beside it: a dispatcher
 * arriving at this screen needs to know there is something to do before
 * they read a list, and a number in a heading is a number that gets scrolled
 * past.
 */
const WEEKDAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DEFAULT_MASK = '1111100';

export function OrderTemplatesPanel() {
  const t = useT();
  const { data, isLoading, isError, error } = useOrderTemplates();
  const generateDue = useGenerateDueTemplates();
  const [editing, setEditing] = useState(null);
  const [result, setResult] = useState(null);
  const [actionError, setActionError] = useState('');

  const templates = data?.templates ?? [];
  const dueCount = data?.due_count ?? 0;

  const runDue = () => {
    setActionError('');
    generateDue.mutate(undefined, {
      onSuccess: setResult,
      onError: (err) => setActionError(err.message),
    });
  };

  return (
    <Panel
      title={t('templates.title')}
      actions={
        <>
          <button type="button" className="btn" onClick={() => setEditing({})}>
            {t('templates.newBtn')}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={dueCount === 0 || generateDue.isPending}
            onClick={runDue}
          >
            {t('templates.generateDue', { n: dueCount })}
          </button>
        </>
      }
    >
      {isError ? <ErrorState error={error} /> : null}
      {actionError ? <ErrorState>{actionError}</ErrorState> : null}
      {result ? <GenerateResult result={result} onDismiss={() => setResult(null)} /> : null}

      {isLoading ? (
        <SkeletonRows rows={3} />
      ) : templates.length === 0 ? (
        <EmptyState>{t('templates.none')}</EmptyState>
      ) : (
        <ul className="space-y-1.5">
          {templates.map((template) => (
            <TemplateRow
              key={template.template_id}
              template={template}
              onEdit={() => setEditing(template)}
              onError={setActionError}
              onGenerated={setResult}
            />
          ))}
        </ul>
      )}

      {editing ? (
        <TemplateForm template={editing} onClose={() => setEditing(null)} />
      ) : null}
    </Panel>
  );
}

function TemplateRow({ template, onEdit, onError, onGenerated }) {
  const t = useT();
  const generate = useGenerateFromTemplate();
  const archive = useArchiveOrderTemplate();

  const run = (force) => {
    onError('');
    generate.mutate(
      { templateId: template.template_id, force },
      {
        onSuccess: (body) =>
          onGenerated({ generated: [{ ...body, name: template.name }], failed: [] }),
        onError: (err) => onError(err.message),
      },
    );
  };

  const days = (template.weekdays ?? '')
    .split('')
    .map((on, i) => (on === '1' ? t(`templates.weekday.${WEEKDAY_KEYS[i]}`) : null))
    .filter(Boolean)
    .join(', ');

  return (
    <li className="rounded-[10px] border border-line bg-white/2 p-2.5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate text-[12.5px] font-semibold">
            {template.name}
            {template.due_today ? (
              <span className="ml-1.5 rounded-full bg-accent/15 px-2 py-0.5 text-[10px] font-bold text-accent-hi">
                {t('templates.dueToday')}
              </span>
            ) : null}
          </div>
          <div className="truncate text-[11px] text-dim">
            {t('templates.summary', {
              cargo: template.cargo_type,
              tons: template.estimated_weight_tons ?? '—',
              loads: template.loads_per_day,
            })}
            {template.scheduled_time ? ` · ${template.scheduled_time}` : ''}
            {/* Nothing at all when the template has no schedule. A dash here
                would read as a value somebody forgot to fill in, and "no
                schedule" is a real and common way to keep a template — a
                saved shape generated from on demand. */}
            {days ? ` · ${days}` : ''}
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            className={`btn !px-2.5 !py-1 !text-[11px] ${template.due_today ? 'btn-primary' : ''}`}
            disabled={generate.isPending}
            onClick={() => run(false)}
          >
            {t('templates.generateBtn')}
          </button>
          <button type="button" className="btn !px-2.5 !py-1 !text-[11px]" onClick={onEdit}>
            {t('common.editBtn')}
          </button>
          <button
            type="button"
            className="btn !px-2.5 !py-1 !text-[11px]"
            disabled={archive.isPending}
            onClick={() => archive.mutate(template.template_id)}
          >
            {t('common.deleteBtn')}
          </button>
        </div>
      </div>
    </li>
  );
}

/**
 * What the run actually did, per template.
 *
 * The failures are rendered with the same weight as the successes, because
 * the route deliberately does not stop on the first one: these are
 * independent standing jobs that happen to fall on the same morning, and
 * refusing eleven clients' loads over a twelfth stale template would be the
 * system holding the day hostage. Which means the failed list IS the
 * dispatcher's to-do list, and burying it under a success count would waste
 * the only reason for reporting per template at all.
 */
function GenerateResult({ result, onDismiss }) {
  const t = useT();
  const orders = (result.generated ?? []).reduce(
    (n, g) => n + (g.created_order_ids?.length ?? 0),
    0,
  );

  return (
    <div role="status" className="mb-3 rounded-[11px] border border-line bg-white/3 p-2.5 text-[11px]">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span>{t('templates.generated', { orders, n: result.generated?.length ?? 0 })}</span>
        <button type="button" className="btn !px-2 !py-0.5 !text-[10px]" onClick={onDismiss}>
          {t('common.closeBtn')}
        </button>
      </div>
      {result.failed?.length ? (
        <ul className="mt-1 space-y-0.5 text-[#FCA5A5]">
          {result.failed.map((f) => (
            <li key={f.template_id}>
              {f.name} — {f.message}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function TemplateForm({ template, onClose }) {
  const t = useT();
  const save = useSaveOrderTemplate();
  const { data: quarries = [] } = useAllQuarries();
  const { data: sites = [] } = useSites();
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    name: template.name ?? '',
    quarry_id: template.quarry_id ?? '',
    site_id: template.site_id ?? '',
    cargo_type: template.cargo_type ?? '',
    estimated_weight_tons: template.estimated_weight_tons ?? '',
    scheduled_time: template.scheduled_time ?? '',
    loads_per_day: template.loads_per_day ?? 1,
    agreed_price_per_ton: template.agreed_price_per_ton ?? '',
    weekdays: template.weekdays ?? DEFAULT_MASK,
  });

  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const toggleDay = (index) => {
    const chars = (form.weekdays || '0000000').split('');
    chars[index] = chars[index] === '1' ? '0' : '1';
    set({ weekdays: chars.join('') });
  };

  const submit = (e) => {
    e.preventDefault();
    setError('');
    save.mutate(
      {
        templateId: template.template_id,
        name: form.name,
        quarry_id: form.quarry_id,
        site_id: form.site_id,
        cargo_type: form.cargo_type,
        // Empty is not zero. A blank tonnage means "not stated", which the
        // server stores as NULL and every order path already understands;
        // sending 0 would be a claim about an empty truck.
        estimated_weight_tons: form.estimated_weight_tons === '' ? null
          : Number(form.estimated_weight_tons),
        agreed_price_per_ton: form.agreed_price_per_ton === '' ? null
          : Number(form.agreed_price_per_ton),
        scheduled_time: form.scheduled_time || null,
        loads_per_day: Number(form.loads_per_day) || 1,
        // An all-zero mask is the no-schedule case, and it is sent as null
        // so the server stores the one thing it means rather than seven
        // falses that look like a half-filled form.
        weekdays: form.weekdays === '0000000' ? null : form.weekdays,
      },
      { onSuccess: onClose, onError: (err) => setError(err.message) },
    );
  };

  return (
    <form onSubmit={submit} className="mt-3 space-y-2 rounded-[11px] border border-accent/40 bg-accent/5 p-3">
      {error ? <ErrorState>{error}</ErrorState> : null}

      <label className="block text-[11px]">
        {t('templates.name')}
        <input className="field" value={form.name} onChange={(e) => set({ name: e.target.value })} required />
      </label>

      <div className="grid gap-2 sm:grid-cols-2">
        <label className="block text-[11px]">
          {t('orders.colQuarry')}
          <select className="field" value={form.quarry_id} onChange={(e) => set({ quarry_id: e.target.value })} required>
            <option value="">—</option>
            {quarries.map((q) => (
              <option key={q.quarry_id} value={q.quarry_id}>{q.name}</option>
            ))}
          </select>
        </label>
        <label className="block text-[11px]">
          {t('orders.colClientSite')}
          <select className="field" value={form.site_id} onChange={(e) => set({ site_id: e.target.value })} required>
            <option value="">—</option>
            {sites.map((s) => (
              <option key={s.site_id} value={s.site_id}>
                {[s.client_name, s.site_name].filter(Boolean).join(' — ')}
              </option>
            ))}
          </select>
        </label>
        <label className="block text-[11px]">
          {t('templates.cargo')}
          <input className="field" value={form.cargo_type} onChange={(e) => set({ cargo_type: e.target.value })} required />
        </label>
        <label className="block text-[11px]">
          {t('templates.tons')}
          <input className="field" type="number" step="0.1" min="0" max="100"
                 value={form.estimated_weight_tons}
                 onChange={(e) => set({ estimated_weight_tons: e.target.value })} />
        </label>
        <label className="block text-[11px]">
          {t('templates.loadsPerDay')}
          <input className="field" type="number" min="1" max="20" value={form.loads_per_day}
                 onChange={(e) => set({ loads_per_day: e.target.value })} />
        </label>
        <label className="block text-[11px]">
          {t('templates.scheduledTime')}
          <input className="field" type="time" value={form.scheduled_time}
                 onChange={(e) => set({ scheduled_time: e.target.value })} />
        </label>
        <label className="block text-[11px]">
          {t('templates.agreedPrice')}
          <input className="field" type="number" step="0.01" min="0"
                 value={form.agreed_price_per_ton}
                 onChange={(e) => set({ agreed_price_per_ton: e.target.value })} />
        </label>
      </div>

      <fieldset className="text-[11px]">
        <legend className="mb-1">{t('templates.weekdays')}</legend>
        <div className="flex flex-wrap gap-1">
          {WEEKDAY_KEYS.map((key, index) => (
            <button
              key={key}
              type="button"
              aria-pressed={form.weekdays?.[index] === '1'}
              onClick={() => toggleDay(index)}
              className={`rounded-[8px] border px-2 py-1 text-[11px] ${
                form.weekdays?.[index] === '1'
                  ? 'border-accent/50 bg-accent/15'
                  : 'border-line bg-white/2'
              }`}
            >
              {t(`templates.weekday.${key}`)}
            </button>
          ))}
        </div>
        <p className="mt-1 text-[10.5px] text-dim">{t('templates.weekdaysHint')}</p>
      </fieldset>

      <div className="flex gap-1.5">
        <button type="submit" className="btn btn-primary" disabled={save.isPending}>
          {t('common.saveBtn')}
        </button>
        <button type="button" className="btn" onClick={onClose}>
          {t('common.cancel')}
        </button>
      </div>
    </form>
  );
}
